import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, Download, Eye, CheckCircle2,
  Clock, AlertCircle, Loader2, Plus, Users, Filter
} from "lucide-react";

const TYPE_LABELS = {
  holerite: "Holerite",
  contrato: "Contrato",
  aviso: "Aviso",
  regulamento: "Regulamento",
  funcionario: "Doc. Funcionário",
  outro: "Outro",
};

const STATUS_BADGE = {
  pending: <Badge variant="outline" className="text-amber-600 border-amber-400 gap-1"><Clock className="w-3 h-3" />Pendente</Badge>,
  signed: <Badge className="bg-emerald-100 text-emerald-800 gap-1"><CheckCircle2 className="w-3 h-3" />Assinado</Badge>,
  employee_upload: <Badge className="bg-blue-100 text-blue-800 gap-1"><Upload className="w-3 h-3" />Enviado por func.</Badge>,
  no_signature_needed: <Badge variant="secondary" className="gap-1"><FileText className="w-3 h-3" />Sem assinatura</Badge>,
};

export function DocumentosTab() {
  const { authAxios } = useAuth();
  const [docs, setDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, signed: 0, employee_uploads: 0 });
  const [loading, setLoading] = useState(true);
  const [filterMember, setFilterMember] = useState("__all__");
  const [filterStatus, setFilterStatus] = useState("__all__");
  const [showUpload, setShowUpload] = useState(false);
  const [showDoc, setShowDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const [form, setForm] = useState({
    member_id: "", title: "", type: "holerite", month: "", requires_signature: true,
  });

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterMember && filterMember !== "__all__") params.set("member_id", filterMember);
      if (filterStatus && filterStatus !== "__all__") params.set("status", filterStatus);
      const [dRes, mRes, sRes] = await Promise.all([
        authAxios.get(`/employee-docs?${params}`),
        authAxios.get("/deliverers/team/members"),
        authAxios.get("/employee-docs/stats"),
      ]);
      setDocs(dRes.data);
      setMembers(mRes.data);
      setStats(sRes.data);
    } catch { }
    finally { setLoading(false); }
  }, [authAxios, filterMember, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Ficheiro demasiado grande (máx 10MB)"); return; }
    setForm(f => ({ ...f, _file: file, _filename: file.name, _content_type: file.type }));
    toast.success(`${file.name} selecionado`);
  };

  const handleUpload = async () => {
    if (!form.member_id || !form.title || !form._file) {
      toast.error("Selecione funcionário, título e ficheiro");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          await authAxios.post("/employee-docs", {
            member_id: form.member_id,
            title: form.title,
            type: form.type,
            month: form.month,
            requires_signature: form.requires_signature,
            file_b64: ev.target.result,
            filename: form._filename,
            content_type: form._content_type || "application/pdf",
          });
          toast.success("Documento enviado!");
          setShowUpload(false);
          setForm({ member_id: "", title: "", type: "holerite", month: "", requires_signature: true });
          fetchAll();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Erro ao enviar");
        }
        setUploading(false);
      };
      reader.readAsDataURL(form._file);
    } catch {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Eliminar "${doc.title}"?`)) return;
    try {
      await authAxios.delete(`/employee-docs/${doc.id}`);
      toast.success("Eliminado");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao eliminar");
    }
  };

  const memberName = (id) => members.find(m => m.id === id)?.name || id;

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total", value: stats.total, icon: FileText, color: "text-foreground" },
          { label: "Pendentes", value: stats.pending, icon: Clock, color: "text-amber-600" },
          { label: "Assinados", value: stats.signed, icon: CheckCircle2, color: "text-emerald-600" },
          { label: "Enviados por func.", value: stats.employee_uploads, icon: Upload, color: "text-blue-600" },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-8 h-8 ${s.color}`} />
              <div>
                <p className="text-2xl font-black">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-40">
          <Label className="text-xs">Funcionário</Label>
          <Select value={filterMember} onValueChange={setFilterMember}>
            <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-36">
          <Label className="text-xs">Estado</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="signed">Assinados</SelectItem>
              <SelectItem value="employee_upload">Enviados por func.</SelectItem>
              <SelectItem value="no_signature_needed">Sem assinatura</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowUpload(true)} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4" />Enviar Documento
        </Button>
      </div>

      {/* Documents list */}
      {docs.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Nenhum documento encontrado</p>
            <Button onClick={() => setShowUpload(true)} variant="outline" className="mt-4 gap-2">
              <Plus className="w-4 h-4" />Enviar primeiro documento
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {docs.map(doc => (
            <Card key={doc.id} className={`border ${doc.status === "pending" ? "border-amber-200" : doc.status === "signed" ? "border-emerald-200" : ""}`}>
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{doc.title}</p>
                    {STATUS_BADGE[doc.status] || <Badge variant="secondary">{doc.status}</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{doc.member_name}</span>
                    <span>{TYPE_LABELS[doc.type] || doc.type}</span>
                    {doc.month && <span>Mês: {doc.month}</span>}
                    <span>{new Date(doc.uploaded_at).toLocaleDateString("pt-BR")}</span>
                    {doc.size_mb && <span>{doc.size_mb}MB</span>}
                  </div>
                  {doc.status === "signed" && doc.signed_at && (
                    <p className="text-xs text-emerald-700 mt-1">
                      ✅ Assinado por <strong>{doc.signed_name}</strong> em {new Date(doc.signed_at).toLocaleString("pt-BR")}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => setShowDoc(doc)} className="gap-1">
                    <Eye className="w-3.5 h-3.5" />Ver
                  </Button>
                  {doc.file_url && (
                    <a href={doc.file_url} target="_blank" rel="noreferrer">
                      <Button variant="outline" size="sm" className="gap-1">
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(doc)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="uppercase font-bold text-sm">Enviar Documento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Funcionário *</Label>
              <Select value={form.member_id || "__none__"} onValueChange={v => setForm(f => ({ ...f, member_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Selecionar funcionário...</SelectItem>
                  {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Título *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Holerite Março 2026" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Mês (opcional)</Label>
                <Input type="month" value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox" id="req-sig"
                checked={form.requires_signature}
                onChange={e => setForm(f => ({ ...f, requires_signature: e.target.checked }))}
                className="w-4 h-4"
              />
              <Label htmlFor="req-sig" className="text-sm cursor-pointer">Requer assinatura do funcionário</Label>
            </div>
            <div>
              <Label className="text-xs">Ficheiro * (PDF, imagem — máx 10MB)</Label>
              <div
                onClick={() => fileRef.current?.click()}
                className="mt-1 border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors"
              >
                {form._filename ? (
                  <p className="text-sm font-medium text-emerald-700">{form._filename}</p>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Clique para selecionar ficheiro</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileSelect} />
            </div>
            <Button onClick={handleUpload} disabled={uploading} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "A enviar..." : "Enviar Documento"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Doc detail dialog */}
      <Dialog open={!!showDoc} onOpenChange={() => setShowDoc(null)}>
        <DialogContent className="max-w-lg">
          {showDoc && (
            <>
              <DialogHeader><DialogTitle className="uppercase font-bold text-sm">{showDoc.title}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><p className="text-xs text-muted-foreground">Funcionário</p><p className="font-medium">{showDoc.member_name}</p></div>
                  <div><p className="text-xs text-muted-foreground">Tipo</p><p className="font-medium">{TYPE_LABELS[showDoc.type] || showDoc.type}</p></div>
                  <div><p className="text-xs text-muted-foreground">Estado</p>{STATUS_BADGE[showDoc.status]}</div>
                  <div><p className="text-xs text-muted-foreground">Enviado em</p><p className="font-medium">{new Date(showDoc.uploaded_at).toLocaleDateString("pt-BR")}</p></div>
                  {showDoc.month && <div><p className="text-xs text-muted-foreground">Mês</p><p className="font-medium">{showDoc.month}</p></div>}
                  {showDoc.uploaded_by_name && <div><p className="text-xs text-muted-foreground">Enviado por</p><p className="font-medium">{showDoc.uploaded_by_name}</p></div>}
                </div>
                {showDoc.status === "signed" && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-1">
                    <p className="text-sm font-bold text-emerald-800">✅ Assinado digitalmente</p>
                    <p className="text-xs text-emerald-700">Nome: <strong>{showDoc.signed_name}</strong></p>
                    <p className="text-xs text-emerald-700">Data/hora: {new Date(showDoc.signed_at).toLocaleString("pt-BR")}</p>
                    {showDoc.signed_ip && <p className="text-xs text-emerald-700">IP: {showDoc.signed_ip}</p>}
                    {showDoc.signature_data && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-1">Assinatura:</p>
                        <img src={showDoc.signature_data} alt="Assinatura" className="border rounded bg-white max-h-24 object-contain" />
                      </div>
                    )}
                  </div>
                )}
                {showDoc.file_url && (
                  <a href={showDoc.file_url} target="_blank" rel="noreferrer" className="block">
                    <Button className="w-full gap-2" variant="outline">
                      <Download className="w-4 h-4" />Baixar Documento
                    </Button>
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
